/**
 * Support ticket list mutation sync (Phase 5A).
 *
 * Contract: docs/list-refactoring-standard.md — prefer in-place row.update /
 * canonical patch of already-loaded rows. Phase 5A UX additionally keeps a
 * loaded row visible after detail→list even when the updated values would no
 * longer match the active filter/sort; full reconcile happens on explicit
 * Refresh / filter / sort / session restart — not as a side-effect of return.
 *
 * @see docs/support-runtime-sync-i18n.md
 */

/**
 * @param {Array<Record<string, unknown>>} loadedRows
 * @param {Record<string, unknown>|null|undefined} ticket
 * @returns {{ rows: Array<Record<string, unknown>>, patched: boolean, identity: number|string|null }}
 */
export function patchLoadedSupportTicketRow(loadedRows, ticket) {
  const rows = Array.isArray(loadedRows) ? loadedRows : [];
  if (!ticket || ticket.id == null) {
    return { rows, patched: false, identity: null };
  }
  const identity = ticket.id;
  let patched = false;
  const next = rows.map((row) => {
    if (row == null || row.id == null) return row;
    if (Number(row.id) !== Number(identity) && String(row.id) !== String(identity)) {
      return row;
    }
    patched = true;
    return { ...row, ...ticket };
  });
  return { rows: next, patched, identity };
}

/**
 * Apply a detail/update ticket onto a Tabulator row when present.
 * Does not insert missing rows and does not delete on filter mismatch.
 *
 * @param {{ getRow?: Function }} table
 * @param {Record<string, unknown>} ticket
 * @returns {'updated'|'missing'|'noop'}
 */
export function updateSupportTicketTabulatorRowInPlace(table, ticket) {
  if (!ticket || ticket.id == null || !table?.getRow) return 'noop';
  const rowComponent = table.getRow(ticket.id);
  if (!rowComponent) return 'missing';
  try {
    const update = rowComponent.update?.(ticket);
    if (update && typeof update.catch === 'function') {
      update.catch(() => {});
    }
  } catch (_) {
    /* ignore */
  }
  return 'updated';
}
